// Builds the wire envelope from track()'s (name, payload). Identity base
// props ride at the top level; everything else is `properties`. user_id and
// admin_id are DELIBERATELY not sent — the server stamps them from the JWT.
import { getFeatureFlagMap } from '@/hooks/useFeatureFlag';
import type { gb as GbInstance } from '@/util/growthbook';

// Lazy require (not a top-level import): `@/util/growthbook` constructs the
// GrowthBook client at module load (`new GrowthBook(...)`). This module is
// reachable from almost any component test via tokenManager → vexo →
// analyticsQueue, so a top-level import here made every such test eagerly
// construct GrowthBook — breaking any test file that locally
// `jest.mock('@growthbook/growthbook-react', ...)`s a partial stub (e.g.
// just `useFeatureValue`) for its own component's needs, since that mock
// then also shadows the constructor `gb` needs (~50 test files do this).
// Resolving it on first actual use, instead, means the constructor only
// runs when an event is actually built — not merely because some unrelated
// component transitively imports this queue.
let cachedGb: typeof GbInstance | undefined;
function getGb(): typeof GbInstance {
  if (!cachedGb) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    cachedGb = (
      require('@/util/growthbook') as typeof import('@/util/growthbook')
    ).gb;
  }
  return cachedGb;
}

// GrowthBook flags worth segmenting analytics by while GB still exists.
// Shrinks to zero as flags migrate to the in-house engine.
export const GB_STAMPED_FLAGS: string[] = [
  'v2.2604.29_vexo-analytics', // FEATURE_TOGGLE.VexoAnalytics — dual-write cohort marker
];

export interface AnalyticsEnvelope {
  event_id: string;
  event_name: string;
  occurred_at: string;
  seq: number;
  session_id: string | null;
  account_id: string | null;
  client_id: string | null;
  role: string | null;
  actor_type: string | null;
  feature_flags: Record<string, unknown> | null; // null = not yet stamped (flag cache cold)
  properties: Record<string, unknown>;
}

const BASE_KEYS = [
  'user_id',
  'admin_id',
  'account_id',
  'client_id',
  'role',
  'actor_type',
  'session_id',
];

export function stampFlags(): Record<string, unknown> | null {
  const inHouse = getFeatureFlagMap();
  if (inHouse === null) return null; // cache cold — queue stamps at flush time
  const stamp: Record<string, unknown> = { ...inHouse };
  for (const key of GB_STAMPED_FLAGS) {
    try {
      stamp[key] = getGb().getFeatureValue(key, null);
    } catch {
      /* analytics never throws */
    }
  }
  return stamp;
}

function newEventId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `ev_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

let seq = 0;

export function buildEnvelope(
  name: string,
  payload: Record<string, unknown>,
): AnalyticsEnvelope {
  const properties: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (!BASE_KEYS.includes(key)) properties[key] = value;
  }
  seq += 1;
  return {
    event_id: newEventId(),
    event_name: name,
    occurred_at: new Date().toISOString(),
    seq,
    session_id: (payload.session_id as string) ?? null,
    account_id: payload.account_id != null ? String(payload.account_id) : null,
    client_id: payload.client_id != null ? String(payload.client_id) : null,
    role: payload.role != null ? String(payload.role) : null,
    actor_type: (payload.actor_type as string) ?? null,
    feature_flags: stampFlags(),
    properties,
  };
}

export function __resetEnvelopeForTests(): void {
  seq = 0;
}
